/**
 * Preparations — the four nervous-system regulation practices.
 *
 * Content designed by Sage (clinical review) and validated by Tim (PMF)
 * and Tracey (brand). These are structured, timer-based flows — no Claude
 * call needed. The AI prescription layer (which prep to surface when) is
 * a Phase 2 Awakening-tier feature; for now suggestion logic lives in
 * src/pages/Home.tsx as a simple rule-based function.
 *
 * Each step has:
 *   instruction — the prompt shown to the user
 *   durationSeconds — how long the step lasts (0 = tap-to-advance, no timer)
 *   cue — optional sub-text beneath the instruction (breath pacing, etc.)
 *
 * ⚠ CLAIM LEVEL — read before editing any user-facing string here.
 * This copy describes what a practice MAY support. It must not assert a
 * physiological outcome the app cannot observe. Earlier wording did exactly
 * that — "Your cortisol is lower", "Your nervous system just registered
 * safety", "Directly activates the ventral vagal", "one of the most
 * evidence-backed regulation tools available" — and was corrected in 2026-08
 * after Marta Stankiewicz independently rewrote every one of them out of the
 * Polish during her review.
 *
 * The rule: describe what the user does and invite them to notice what
 * changes. Do not tell them what happened inside their body.
 */

export type PreparationId = 'orienting' | 'coherent-heart' | 'toning' | 'shake' | 'affirmations'

export interface PreparationStep {
  instruction: string
  durationSeconds: number
  cue?: string
  /** Render as a soft interstitial pause rather than an affirmation card */
  isBreathPrompt?: boolean
  /** Category label shown above affirmation cards */
  category?: string
  /** Show heart-save icon — Practice+ users can save to Inner Sanctuary */
  isSaveable?: boolean
}

export interface Preparation {
  id: PreparationId
  name: string
  tagline: string
  description: string
  durationMinutes: number
  color: string            // accent color for cards and borders
  colorSoft: string        // rgba background for cards
  stepCount: number
  steps: PreparationStep[]
  completionLine: string   // shown on the finish screen
  /**
   * Plain-language note on what the practice may support. Deliberately
   * possibility rather than asserted outcome — see the header note on claims.
   */
  regulation: string
  /** Attribution line shown on card screens and completion — e.g. "Curated by Marta Stankiewicz" */
  attribution?: string
  /** Enables affirmation-specific rendering (card-based, heart-save, tier gating) */
  isAffirmations?: boolean
  /** Max steps shown to free-tier users before the upsell screen */
  freeTierStepCount?: number
}

export const PREPARATIONS: Preparation[] = [
  {
    id: 'orienting',
    name: 'Orienting',
    tagline: 'Return to the present moment through contact with your surroundings.',
    description: 'Your nervous system is always taking in information about what is around you. Orienting is a short practice of looking around deliberately, noticing the space, and bringing your attention back to what is here right now. It takes about two minutes.',
    durationMinutes: 2,
    color: '#3A9B7A',
    colorSoft: 'rgba(58,155,122,0.06)',
    stepCount: 5,
    regulation: 'Orienting to your surroundings can support a sense of presence and contact by directing attention to signals from the space you are actually in.',
    steps: [
      {
        instruction: 'Find a comfortable position — seated or standing. Let your hands rest.',
        durationSeconds: 0,
        cue: 'Tap when you\'re ready.',
      },
      {
        instruction: 'Take one slow breath. Feel your feet on the floor, your weight in the seat.',
        durationSeconds: 15,
        cue: 'Just arrive here.',
      },
      {
        instruction: 'Let your eyes move slowly around the room. Not looking for anything — just noticing.',
        durationSeconds: 40,
        cue: 'Let your eyes land where they want.',
      },
      {
        instruction: 'When something catches your attention, let your gaze rest there. Notice the color. The shape. The texture.',
        durationSeconds: 35,
        cue: 'There\'s nowhere else to be.',
      },
      {
        instruction: 'Come back to center. Take a breath. Notice how your body feels right now, compared to when you began.',
        durationSeconds: 20,
        cue: 'That\'s it. That\'s the practice.',
      },
    ],
    completionLine: 'Your nervous system just had a moment to notice what is here, right now.',
  },

  {
    id: 'coherent-heart',
    name: 'Heart Breathing',
    tagline: 'Bring your breath into a gentle, even rhythm.',
    description: 'Slow, even breathing can support nervous-system regulation and help the body settle into a calmer, more present state. For a few minutes, let the breath find a gentle, even rhythm and notice how your body responds.',
    durationMinutes: 5,
    color: '#C45272',
    colorSoft: 'rgba(196,82,114,0.06)',
    stepCount: 4,
    regulation: 'Slow, rhythmic breathing can support the processes involved in regulating the autonomic nervous system, and may bring a greater sense of calm and presence.',
    steps: [
      {
        instruction: 'Sit comfortably. Place one hand on your chest if you like.',
        durationSeconds: 0,
        cue: 'Tap to begin the breath rhythm.',
      },
      {
        instruction: 'Breathe in for 5... and out for 5...',
        durationSeconds: 180,
        cue: 'In... 2... 3... 4... 5... Out... 2... 3... 4... 5...',
      },
      {
        instruction: 'Let your breath return to its natural rhythm. Don\'t control it — just observe.',
        durationSeconds: 30,
        cue: 'Notice the stillness.',
      },
      {
        instruction: 'Before you move on — notice the quality of your state right now.',
        durationSeconds: 20,
        cue: 'Something in the body has shifted.',
      },
    ],
    completionLine: 'Take a moment and notice how your breath, your body and your inner state feel now.',
  },

  {
    id: 'toning',
    name: 'Toning',
    tagline: 'Use your voice and its vibration to support quiet and presence.',
    description: 'Humming and sustained sound create a gentle vibration felt in the throat, the chest and the body. For many people, working with the voice this way can support quiet, contact with the body, and a greater sense of presence. About three minutes.',
    durationMinutes: 3,
    color: '#B87D0A',
    colorSoft: 'rgba(184,125,10,0.06)',
    stepCount: 5,
    regulation: 'An extended exhale combined with sound can support regulation through breath, vibration and conscious contact with the body.',
    steps: [
      {
        instruction: 'Find somewhere you can make sound without self-consciousness. No one needs to hear this but you.',
        durationSeconds: 0,
        cue: 'Tap when you\'re ready.',
      },
      {
        instruction: 'Place one hand on your chest. Take a breath in.',
        durationSeconds: 10,
        cue: 'Feel your hand rise.',
      },
      {
        instruction: 'On the exhale — hm. Low and easy. No effort, no performance. Just the sound.',
        durationSeconds: 60,
        cue: 'Feel the vibration in your chest and throat.',
      },
      {
        instruction: 'Let the sound change if it wants to. It might become an "ahhh" or an "ohhhh." Follow what arises.',
        durationSeconds: 75,
        cue: 'You can\'t do this wrong.',
      },
      {
        instruction: 'Silence now. Sit with the resonance still moving through you.',
        durationSeconds: 25,
        cue: 'Notice what\'s different.',
      },
    ],
    completionLine: 'Let yourself feel, for a moment, the quiet and the resonance the sound left behind.',
  },

  {
    id: 'shake',
    name: 'Shake Practice',
    tagline: 'Let your body move the tension it has been holding.',
    description: 'Tension can be felt in different parts of the body — the shoulders, the jaw, the chest, the belly, the hips. Deliberate shaking brings movement into places that may be tight or held. Let your body move freely and notice how it responds.',
    durationMinutes: 5,
    color: '#4A80C0',
    colorSoft: 'rgba(74,128,192,0.06)',
    stepCount: 6,
    regulation: 'Free, conscious movement can help you notice and loosen tension in the body, while supporting contact with breath, movement and physical sensation.',
    steps: [
      {
        instruction: 'Stand if you can, or sit with room to move. This might feel awkward. That\'s fine.',
        durationSeconds: 0,
        cue: 'Tap when you\'re ready.',
      },
      {
        instruction: 'Start with your hands — shake them quickly and loosely, like you\'re trying to shake water off.',
        durationSeconds: 30,
        cue: 'Loose. Fast. Let the wrists be floppy.',
      },
      {
        instruction: 'Let the shaking travel up through your arms and into your shoulders.',
        durationSeconds: 40,
        cue: 'Don\'t control it — let the movement find its own path.',
      },
      {
        instruction: 'Now your whole upper body. Your hips if they want to join. Your whole self. Let your body be ridiculous.',
        durationSeconds: 90,
        cue: 'You cannot do this wrong.',
      },
      {
        instruction: 'Now stop. Completely still.',
        durationSeconds: 60,
        cue: 'Close your eyes. What do you notice?',
      },
      {
        instruction: 'Take a breath. Let it land.',
        durationSeconds: 20,
        cue: 'Feel what changed.',
      },
    ],
    completionLine: 'Take a moment and notice how your body feels after moving.',
  },

  {
    id: 'affirmations',
    name: 'Affirmations for Self-Forgiveness',
    tagline: 'Words that return you to yourself.',
    description: 'Five categories of affirmations — emotional, body-based, inner child, reclaiming power, and soul-level. Move through them slowly. Let each one land before continuing.',
    durationMinutes: 5,
    color: '#9B72C0',
    colorSoft: 'rgba(155,114,192,0.06)',
    stepCount: 11,
    regulation: 'These affirmations pair words with felt sense in the body and conscious presence. The aim is to deepen contact with yourself, your emotions and your own truth.',
    attribution: 'Curated by Marta Stankiewicz · Awaken By Love',
    isAffirmations: true,
    freeTierStepCount: 4,
    completionLine: 'These words can become part of your inner language from here.',
    steps: [
      {
        instruction: 'I am allowed to feel what I feel.',
        durationSeconds: 0,
        category: 'Emotional',
        isSaveable: true,
      },
      {
        instruction: 'My emotions are not too much. They are asking to be held.',
        durationSeconds: 0,
        category: 'Emotional',
        isSaveable: true,
      },
      {
        instruction: 'My body held what my heart couldn\'t speak.',
        durationSeconds: 0,
        category: 'Body',
        isSaveable: true,
      },
      {
        instruction: 'I return to this body as a place of safety.',
        durationSeconds: 0,
        category: 'Body',
        isSaveable: true,
      },
      {
        // Interstitial breath before the Inner Child category — Sage requirement
        instruction: 'This one may go deep. Take it slowly.',
        durationSeconds: 0,
        isBreathPrompt: true,
        cue: 'A breath before you continue.',
      },
      {
        instruction: 'I see you, younger self. I\'m sorry I left you.',
        durationSeconds: 0,
        category: 'Inner Child',
        isSaveable: true,
      },
      {
        instruction: 'You didn\'t deserve what happened. I\'m here now.',
        durationSeconds: 0,
        category: 'Inner Child',
        isSaveable: true,
      },
      {
        instruction: 'My worthiness was never theirs to give or take.',
        durationSeconds: 0,
        category: 'Reclaiming Power',
        isSaveable: true,
      },
      {
        instruction: 'I take back what was always mine.',
        durationSeconds: 0,
        category: 'Reclaiming Power',
        isSaveable: true,
      },
      {
        instruction: 'I release the story that diminished me.',
        durationSeconds: 0,
        category: 'Soul',
        isSaveable: true,
      },
      {
        instruction: 'I am not the wound. I am what survived it.',
        durationSeconds: 0,
        category: 'Soul',
        isSaveable: true,
      },
    ],
  },
]

/** Look up a preparation by id */
export function getPreparation(id: PreparationId): Preparation {
  return PREPARATIONS.find(p => p.id === id)!
}

/**
 * Rule-based suggestion for the home page Option B card.
 * Phase 2: replace this with Claude-driven prescription for Awakening tier.
 *
 * Logic:
 *   No sessions → Orienting (default welcome)
 *   < 1 day since last session → Heart Breathing (integration)
 *   1–3 days → Toning (still processing)
 *   3+ days + high intensity (≥8) → Shake (discharge before returning)
 *   3+ days, normal → Orienting (ground before going back in)
 */
export function suggestPreparation(
  lastSessionDate: Date | null,
  lastSessionIntensity: number,
): { preparation: Preparation; reason: string } {
  if (!lastSessionDate) {
    return {
      preparation: getPreparation('orienting'),
      reason: 'Ground yourself before you begin.',
    }
  }

  const daysSince = (Date.now() - lastSessionDate.getTime()) / (1000 * 60 * 60 * 24)

  if (daysSince < 1) {
    return {
      preparation: getPreparation('coherent-heart'),
      reason: 'Integrate what you just worked through.',
    }
  }

  if (daysSince < 3) {
    return {
      preparation: getPreparation('toning'),
      reason: 'Let the work continue through sound.',
    }
  }

  if (lastSessionIntensity >= 8) {
    return {
      preparation: getPreparation('shake'),
      reason: 'Discharge what\'s been building before you go back in.',
    }
  }

  return {
    preparation: getPreparation('orienting'),
    reason: 'Settle your nervous system before the next session.',
  }
}
